import { formatMeetingDate } from '../date-formatters/index.js';
export * from './minutes.js';
export const MEETING_ESTIMATED_DURATION_MINUTES = 14;
/**
 * Builds a recording/asset title from meeting event data.
 *
 * Shared by every path that names a Mux recording asset — legacy single-meeting streaming, manual
 * uploads, broadcast start, and the broadcast split job — so all recordings use one convention.
 *
 * Format: `"{Meeting Title} {MM/DD/YYYY}"`.
 *
 * @param meetingEvent - The meeting event's title and date.
 * @returns The recording title.
 */
export function buildRecordingTitle(meetingEvent) {
    const dateStr = formatMeetingDate(meetingEvent.date, { format: 'MM/dd/yyyy' });
    return `${meetingEvent.title} ${dateStr}`;
}
/**
 * Gets the scheduled end date for a stream: the configured duration measured from the later of the
 * meeting's scheduled start and when it actually went live.
 *
 * Going live before the scheduled start — an auto-start promote/enable lead, or a clerk who clicks Go
 * Live early — is additive warm-up that never shortens the streaming window: a meeting scheduled at
 * 4:50 with a 15-minute duration that starts at 4:49 still ends at 5:05, not 5:04. Starting on time
 * or late anchors to `startedAt`, preserving the full duration from the actual start. Callers that
 * have no scheduled start (or pass none) keep the plain `startedAt + duration` behavior.
 *
 * @param startedAt - When the meeting actually went live.
 * @param streamDurationSeconds - Planned stream duration; null/0 yields no scheduled end.
 * @param scheduledStartDate - The meeting's scheduled start; when it is later than `startedAt`, the
 * duration is measured from it so an early start does not eat into the window.
 */
export const getStreamScheduledEndDate = (startedAt, streamDurationSeconds, scheduledStartDate) => {
    if (!streamDurationSeconds) {
        return null;
    }
    const anchor = scheduledStartDate && scheduledStartDate.getTime() > startedAt.getTime()
        ? scheduledStartDate
        : startedAt;
    return new Date(anchor.getTime() + streamDurationSeconds * 1000);
};
